// PostHog product analytics for Chipless.
//
// Strategy (see ANALYTICS.md): add one custom event at a time, reconcile
// against RTDB ground-truth counters, then move on. Live custom events:
//   - hand_started        (#6 Hands dealt) — host-only
//   - game_started        (#1 Games started) — host-only
//   - game_ended          (#2 Games ended — host End Game settle only)
//   - game_player_active  (per-player game entry) — EVERY player's device,
//                         once per game per device; no RTDB counterpart
//
// Autocapture ($pageview etc.) plus posthog.identify(uid) cover daily usage,
// traffic sources, and browser/device types with no custom events.
//
// Privacy: live room join codes must never reach third parties. game_id is a
// pseudonymized hash of roomCode + createdAt (see getGameId for what that does
// and does not buy), and index.js sanitizes /join/:code URLs out of every event
// before it is sent.
import posthog from 'posthog-js';
import { ensureAuth } from './auth';
import { playerNamesFrom, shouldCountStats } from './shouldCountStats';

/**
 * Deterministic pseudonymous id for one game.
 *
 * Room codes are recycled (rooms are deleted ~10 min after inactivity by the
 * cleanup GitHub Action), so the code alone must never key a game — createdAt
 * disambiguates reuse. The pair is hashed (djb2) so the live join code itself
 * is not sent to PostHog.
 *
 * NOT a one-way hash. djb2 is a 32-bit non-cryptographic hash over a 5-char
 * code from a 31-char alphabet (~28.6M), and an attacker holding a game_id also
 * holds the event timestamp, which pins createdAt — so candidate codes are
 * brute-forceable in seconds. What makes that uninteresting: 32 bits of output
 * yields thousands of colliding candidates rather than one answer, a recovered
 * code is only usable inside the room's ~10-minute TTL, and you need PostHog
 * data access to start. This is pseudonymization — keeping live codes out of
 * analytics payloads — not a security boundary. Real one-wayness would take
 * HMAC-SHA256 with a build-time secret, which is async (Web Crypto) and would
 * change every game_id, breaking joins against historical events.
 *
 * @param {object} room - Synced room object (needs roomCode + createdAt)
 * @returns {string|null} e.g. "game_1x8k3fz" or null when unavailable
 */
export function getGameId(room) {
  if (!room?.roomCode) return null;
  const seed = `${room.roomCode}:${room.createdAt || 0}`;
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash + seed.charCodeAt(i)) >>> 0; // hash * 33 + c
  }
  return `game_${hash.toString(36)}`;
}

// Resolves once posthog.identify(uid) has run for this page load. With
// persistence: 'memory' the anonymous distinct_id is regenerated on every
// load, so a capture that races ahead of identify lands on a throwaway id
// that can never be stitched back to the user. Mount-time captures must
// await this; click-path events inherit the App.jsx startup call, which
// warms this same cached promise.
let identifyPromise = null;

export function ensureIdentified() {
  if (!process.env.REACT_APP_POSTHOG_KEY) return Promise.resolve(null);
  if (!identifyPromise) {
    identifyPromise = ensureAuth()
      .then((uid) => {
        if (uid) posthog.identify(uid);
        return uid;
      })
      .catch((error) => {
        console.warn('📊 PostHog identify failed:', error);
        identifyPromise = null; // allow a later call to retry
        return null;
      });
  }
  return identifyPromise;
}

// Once-per-game-per-device guard for game_player_active. localStorage dedups
// across refresh/rejoin remounts; the in-memory set closes the window where
// two concurrent calls (e.g. StrictMode's dev double-mount) both pass the
// localStorage check before either writes. Only hashed game ids are stored —
// never room codes — and PostHog's own persistence stays 'memory'.
const PLAYER_ACTIVE_KEY = 'chipless:pha:player_active';
const PLAYER_ACTIVE_MAX = 5;
const playerActiveInFlight = new Set();

function hasFiredPlayerActive(gameId) {
  try {
    const raw = localStorage.getItem(PLAYER_ACTIVE_KEY);
    if (!raw) return false;
    const fired = JSON.parse(raw);
    return Array.isArray(fired) && fired.includes(gameId);
  } catch {
    return false;
  }
}

function markFiredPlayerActive(gameId) {
  try {
    const raw = localStorage.getItem(PLAYER_ACTIVE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const fired = Array.isArray(parsed) ? parsed : [];
    fired.push(gameId);
    localStorage.setItem(PLAYER_ACTIVE_KEY, JSON.stringify(fired.slice(-PLAYER_ACTIVE_MAX)));
  } catch {
    // localStorage unavailable — the in-memory set still dedups this load.
  }
}

/**
 * Capture `game_player_active` — this device entered a live game.
 *
 * Unlike the host-only events below, this fires on EVERY player's device when
 * GameBoard mounts, so hosts and joiners are both counted (`is_host` splits
 * them). Because it fires on mount rather than behind a click, it must await
 * identify first — otherwise the event would be orphaned on a per-load
 * anonymous id. Fired at most once per game per device: a refresh or rejoin
 * remounts GameBoard but must not inflate per-game entry counts.
 *
 * @param {object} room - Synced room object (for game_id)
 * @param {boolean} isHost - Whether this viewer is the game's host
 */
export async function captureGamePlayerActive(room, isHost) {
  const gameId = getGameId(room);
  if (!gameId) return;
  if (playerActiveInFlight.has(gameId) || hasFiredPlayerActive(gameId)) return;
  playerActiveInFlight.add(gameId);
  try {
    const uid = await ensureIdentified();
    if (process.env.REACT_APP_POSTHOG_KEY && !uid) {
      // Identify failed — capturing now would orphan the event on an
      // anonymous id. Release the guard so a later remount can retry.
      playerActiveInFlight.delete(gameId);
      return;
    }
    posthog.capture('game_player_active', {
      game_id: gameId,
      is_host: !!isHost,
    });
    markFiredPlayerActive(gameId);
  } catch (error) {
    playerActiveInFlight.delete(gameId);
    // Analytics must never break gameplay.
    console.warn('📊 PostHog game_player_active capture failed:', error);
  }
}

/**
 * Capture `hand_started` — every hand dealt (stat #6).
 *
 * Called from the host device only (start-game and deal-next-hand handlers
 * both run exclusively on the host), which matches the host-only gating of
 * the RTDB trackNewHand counter — so PostHog and RTDB should reconcile 1:1.
 *
 * @param {object} room - Synced room object (for game_id)
 * @param {number} handNumber - 1-based hand number (hand #1 included!)
 * @param {number} activePlayerCount - Players dealt into this hand
 */
export function captureHandStarted(room, handNumber, activePlayerCount) {
  try {
    const gameId = getGameId(room);
    if (!gameId) return;
    posthog.capture('hand_started', {
      game_id: gameId,
      hand_number: handNumber,
      active_player_count: activePlayerCount,
    });
  } catch (error) {
    // Analytics must never break gameplay.
    console.warn('📊 PostHog hand_started capture failed:', error);
  }
}

/**
 * Capture `game_started` — room reached first hand / PLAYING (stat #1).
 *
 * Called from the host device only, next to RTDB trackGameStart, so the two
 * counters reconcile. Fires once when the host starts the first hand.
 *
 * @param {object} room - Synced room object (for game_id)
 * @param {number} playerCount - Approved players at game start
 * @param {number} smallBlind - Small blind in cents
 * @param {number} bigBlind - Big blind in cents
 */
export function captureGameStarted(room, playerCount, smallBlind, bigBlind) {
  try {
    const gameId = getGameId(room);
    if (!gameId) return;
    posthog.capture('game_started', {
      game_id: gameId,
      player_count: playerCount,
      small_blind: smallBlind,
      big_blind: bigBlind,
    });
  } catch (error) {
    console.warn('📊 PostHog game_started capture failed:', error);
  }
}

/**
 * Capture `feedback_submitted` — a feedback entry was written to RTDB.
 *
 * Fires on EVERY device (not host-only) from `submitFeedback` after the RTDB
 * write succeeds, so all entry points (home footer, game board link, end-game
 * rating card) share one code path. Carries the raw message text AND the
 * reply email by owner preference, so feedback is fully manageable from
 * PostHog without cross-referencing RTDB.
 *
 * @param {object} params
 * @param {string} params.source - Entry point (e.g. 'footer', 'game_board', 'end_game')
 * @param {'up'|'down'|null} params.rating - Binary thumbs rating, if any
 * @param {string|null} params.message - Raw feedback text, if any
 * @param {string|null} params.email - Reply email, if the user left one
 */
export function captureFeedbackSubmitted({ source, rating = null, message = null, email = null }) {
  try {
    posthog.capture('feedback_submitted', {
      source: source || 'unknown',
      rating,
      message,
      email,
      has_email: !!email,
    });
  } catch (error) {
    console.warn('📊 PostHog feedback_submitted capture failed:', error);
  }
}

/**
 * Capture `game_ended` — host formally settled the game (stat #2).
 *
 * ONLY called from the host End Game confirm path (settlements). Do not fire
 * on auto-end, host leave, or abandoned-room cleanup.
 *
 * @param {object} room - Synced room object (for game_id)
 * @param {number} handCount - Hands dealt in this game
 * @param {string} endedBy - Currently always 'host' (settle path)
 */
export function captureGameEnded(room, handCount, endedBy = 'host') {
  try {
    const gameId = getGameId(room);
    if (!gameId) return;
    posthog.capture('game_ended', {
      game_id: gameId,
      hand_count: handCount,
      ended_by: endedBy,
    });
  } catch (error) {
    console.warn('📊 PostHog game_ended capture failed:', error);
  }
}

function captureRebuyEvent(event, room, extra = {}) {
  try {
    if (!shouldCountStats({ playerNames: playerNamesFrom(room?.players) })) return;
    const gameId = getGameId(room);
    if (!gameId) return;
    posthog.capture(event, { game_id: gameId, ...extra });
  } catch (error) {
    console.warn(`📊 PostHog ${event} capture failed:`, error);
  }
}

/** Player tapped Rebuy on their phone (a request, not chips yet). */
export function captureRebuyRequested(room, amountCents) {
  captureRebuyEvent('rebuy_requested', room, { amount_cents: amountCents });
}

/** Host added chips for a pending phone request, or via Host tools rebuy. */
export function captureRebuyAdded(room, amountCents) {
  captureRebuyEvent('rebuy_added', room, { amount_cents: amountCents });
}

/** Player withdrew a pending phone-rebuy before the host tapped Add. */
export function captureRebuyCancelled(room) {
  captureRebuyEvent('rebuy_cancelled', room);
}

/** Busted player tapped Spectate (or the pill's Rebuy is a different event). */
export function captureSpectateTapped(room) {
  captureRebuyEvent('spectate_tapped', room);
}
